import { inject, Injectable } from '@angular/core'
import { getDownloadURL as _getDownloadURL, getMetadata, list, ListOptions, ref, Storage, StorageReference } from '@angular/fire/storage'
import { environment } from '@nx-superprep/environment'
import { AssetType, Resource } from '../models/front-end'
import { UserService } from './user.service'
import { runThroughArguments } from './run-through-arguments'
import { log } from '@nx-superprep/utils'

export type ShapeType = 'round'|'rect'

export interface AssetReference {
  ref?: StorageReference,
  url: string,
  cached?: boolean,
}

@Injectable({providedIn: 'root'})
export class StorageService {

  readonly userService = inject(UserService)
  readonly storage = inject(Storage)

  constructor() {
    this.userService.userData.subscribe((user) => { if(user && user?.uid !== this._uid) { this.clear(); this._uid = user.uid }})
  }

  private readonly _missingAsset = 'assets/missing'
  private readonly _cachedRefMap = new Map<string, AssetReference>()

  //! densities available on backend: [@2x, @2x~iphone, @3x~iphone]
  private readonly _densityMap = new Map<string, string>([
    ['1','@2x'],
    ['2','@2x~iphone'],
    ['3','@3x~iphone'],
  ])

  private _densityCache: string | undefined

  private _density(): string {
    return this._densityCache ??= '@3x~iphone'
  }

  private readonly _suffixCache: Map<string, string> = new Map()
  private _suffixMapping = {
    round: [
      {design: '32x32', resolution:	'32x32', suffix: '_32x32'},
      {design: '56x56', resolution:	'56x56', suffix: '_56x56'},
      {design: '64x64', resolution:	'64x64', suffix: '_64x64'},
      {design: '84x84', resolution:	'84x84', suffix: '_84x84'},
      {design: '96x96', resolution:	'96x96', suffix: '_96x96'},
      {design: '112x112', resolution:	'112x112', suffix: '_112x112'},
      {design: '168x168', resolution:	'168x168', suffix: '_168x168'},
      {design: '252x252', resolution:	'252x252', suffix: '_252x252'},
    ],
    rect: [
      {design: '142x142', resolution:'274x149', suffix:'_274x149'},
      {design: '156x156', resolution:'274x149', suffix:'_274x149'},
      {design: '274x124', resolution:'274x149', suffix:'_274x149'},
      {design: '276x200', resolution:'368x200', suffix:'_368x200'},
      {design: '285x285', resolution:'524x285', suffix:'_524x285'},
      {design: '343x200', resolution:'368x200', suffix:'_368x200'},
    ]
  }

  private _suffix(shape: ShapeType, resolution: string): string {
    const key = `${shape}-${resolution}`
    if (this._suffixCache.has(key)) {
      return this._suffixCache.get(key) as string
    }
    const suffix = (this._suffixMapping[shape].find(s => s.resolution === resolution) ?? this._suffixMapping[shape][4]).suffix
    this._suffixCache.set(key, suffix)
    return suffix
  }

  private _uid = ''

  basePath = `gs://${environment.useEmulators ? 'demo-superprep-dev.appspot.com' : this.storage.app.options.storageBucket}`

  clear() {
    this._cachedRefMap.clear()
    this._suffixCache.clear()
  }

  async getIconUrl(name: string): Promise<string> {
    const cached = this._cachedRefMap.get(name)
    if (cached && cached.url) {
      return cached.url
    }

    const fallbackUrl = `${this._missingAsset}.png`
    try {
      const asset = await this.getIconAsset(name)
      return asset.url.length > 0 ? asset.url : fallbackUrl
    } catch (error) {
      log.debug(error)
      return fallbackUrl
    }
  }

  // Path ---------------------------------------------------------------------------------------

  /**
   * Generates the path for various types of assets based on provided parameters.
   *
   * @param type The type of asset. Can be 'audio', 'diagram', 'icon', 'image', or 'video'.
   * @param extension The file extension of the asset.
   * @param name The name of the asset. If provided, it can include a path, which will be processed to extract the actual name.
   * @param resolution The resolution of the asset. Applicable for 'icon' type. Default is '84x84' for round icons and '368x200' for other shapes.
   * @param shape The shape of the asset. Applicable for 'icon' type. Default is 'round'.
   * @returns The complete path for the asset.
   */
  getAssetPath(type: 'audio' | 'diagram' | 'icon' | 'image' | 'video', extension: string, name?: string, resolution?: string, shape: ShapeType = 'round') {
    const cached = this._cachedRefMap.get(name || '')
    if (cached) return cached.url

    if (!name) return undefined

    // Extract the actual name from the provided path and remove any trailing '-rectangle' suffix if applicable
    const formattedName = name.split('/').pop()?.replace('-rectangle', '')
    if (!formattedName) return undefined

    resolution ||= shape === 'round' ? '96x96' : '368x200'
    const suffix = this._suffix(shape, resolution)

    let path
    // Construct the asset path based on the type
    switch (type) {
      case 'icon':
        path = `${shape}/resized/${formattedName}${suffix}.${extension}`
        break
      case 'image':
        path = `migrated/${formattedName}${this._density()}.${extension}`
        break
      case 'video':
        path = `transcoded/${formattedName}_500x500.${extension}`
        break
      default:
        path = `migrated/${formattedName}.${extension}`
    }

    return `${this.basePath}/assets/${type}s/${extension}/${path}`
  }

  // Reference ---------------------------------------------------------------------------------------

  /**
   * Generates the folder path for a specified type of asset.
   *
   * @param assetType The type of asset.
   * @returns The folder path for the specified asset type.
   */
  getAssetFolder(assetType: AssetType) {
    // Define the folder path based on the asset type
    const folder = ((assetType: AssetType) => {
      switch (assetType) {
        case 'audio': return `audios/mp3/migrated`; // Folder path for audio assets
        case 'svg': return `diagrams/svg/migrated`; // Folder path for SVG assets
        case 'icon': return `icons/png/rect/resized`; // Folders path for icon assets
        case 'image': return `images/png/migrated`; // Folder path for image assets
        case 'video': return `videos/mp4/transcoded`; // Folder path for video assets
        default: return `migrated`; // Default folder path
      }
    })(assetType)

    // Return the complete folder path constructed using the base path and the determined folder
    return `${this.basePath}/assets/${folder}`
  }

  /**
   * Generates a reference to an asset stored in cloud storage based on the provided parameters.
   *
   * @param type The type of asset. Can be 'audio', 'diagram', 'icon', 'image', or 'video'.
   * @param extension The file extension of the asset.
   * @param name The name of the asset. If provided, it can include a path.
   * @param resolution The resolution of the asset. Applicable for 'icon' type.
   * @param shape The shape of the asset. Applicable for 'icon' type.
   * @returns A reference to the asset stored in cloud storage.
   */
  getAssetRef(type: 'audio' | 'diagram' | 'icon' | 'image' | 'video', extension: string, name?: string, resolution?: string, shape: ShapeType = 'round'): StorageReference {
    // If the name starts with '/assets', it's already a reference, so return it
    if (name?.startsWith('/assets')) {
      return { name: name?.split('/').pop(), fullPath: name } as StorageReference
    }

    // Otherwise, generate a path for the asset and create a reference
    const path = this.getAssetPath(type, extension, name, resolution, shape)
    return path ? ref(this.storage, path) : { name, fullPath: name } as StorageReference
  }

  /**
   * Generates a reference to an audio asset stored in cloud storage.
   *
   * @param name The name of the audio asset.
   * @returns A reference to the audio asset stored in cloud storage.
   */
  getAudioRef(name: string | undefined): StorageReference {
    return this.getAssetRef('audio', 'mp3', name)
  }

  /**
   * Generates a reference to a diagram asset stored in cloud storage.
   *
   * @param name The name of the diagram asset.
   * @returns A reference to the diagram asset stored in cloud storage.
   */
  getDiagramRef(name: string | undefined): StorageReference {
    return this.getAssetRef('diagram', 'svg', name)
  }

  /**
   * Generates a reference to an icon asset stored in cloud storage.
   *
   * @param name The name of the icon asset.
   * @param resolution The resolution of the icon asset.
   * @param shape The shape of the icon asset.
   * @returns A reference to the icon asset stored in cloud storage.
   */
  getIconRef(name: string | undefined, resolution?: string, shape?: ShapeType): StorageReference {
    return this.getAssetRef('icon', 'png', name, resolution, shape ?? 'round')
  }

  /**
   * Generates a reference to an image asset stored in cloud storage.
   *
   * @param name The name of the image asset.
   * @returns A reference to the image asset stored in cloud storage.
   */
  getImageRef(name: string | undefined): StorageReference {
    return this.getAssetRef('image', 'png', name)
  }

  /**
   * Generates a reference to a video asset stored in cloud storage.
   *
   * @param name The name of the video asset.
   * @returns A reference to the video asset stored in cloud storage.
   */
  getVideoRef(name: string | undefined): StorageReference {
    return this.getAssetRef('video', 'mp4', name)
  }

  /**
   * Generates a reference to an asset based on its type and name.
   *
   * @param item The resource containing information about the asset.
   * @returns A reference to the asset stored in cloud storage.
   */
  getRefForAsset(item: Resource): StorageReference | undefined {
    switch(item.assetType) {
      case AssetType.audio: return this.getAudioRef(item.name)
      case AssetType.video: return this.getVideoRef(item.name)
      case AssetType.image: return this.getImageRef(item.name)
      case AssetType.svg: return this.getDiagramRef(item.name)
      case AssetType.icon: return this.getIconRef(item.name)
      default: return undefined; // Return undefined for unsupported asset types
    }
  }

  /**
   * Retrieves metadata for an asset stored in cloud storage.
   *
   * @param type The type of asset. Can be 'audio', 'diagram', 'icon', 'image', or 'video'.
   * @param extension The file extension of the asset.
   * @param name The name of the asset. If provided, it can include a path.
   * @param resolution The resolution of the asset. Applicable for 'icon' type.
   * @param shape The shape of the asset. Applicable for 'icon' type.
   * @returns A Promise resolving to the metadata of the asset, or a string representing the path if metadata is not available.
   */
  async getAssetMeta(type: 'audio' | 'diagram' | 'icon' | 'image' | 'video', extension: string, name?: string, resolution?: string, shape: ShapeType = 'round') {
    const storageRef = this.getAssetRef(type, extension, name, resolution, shape)

    // If the asset is locally provided, return its path
    if (!storageRef.storage) {
      return Promise.resolve(storageRef.fullPath)
    }

    // If the asset is provided by Google Cloud Storage, retrieve its metadata
    try {
      const meta = await getMetadata(storageRef)
      return meta
    } catch (error) {
      log.debug(error)
      // If metadata retrieval fails, return a path to a placeholder image or audio file
      return `${this._missingAsset}.${type === 'audio' ? 'mp3' : 'svg'}`
    }
  }

  /**
   * Retrieves the duration of an audio asset stored in cloud storage.
   *
   * @param name The name of the audio asset.
   * @returns A Promise resolving to the duration of the audio asset in seconds.
   */
  getAudioDuration(name: string) {
    return this.getAssetMeta('audio', 'mp3', name).then(meta =>
      typeof meta === 'string' ? 0 : meta.customMetadata ? Number(meta.customMetadata['duration']) || 0 : 0
    )
  }

  // URLs ---------------------------------------------------------------------------------------

  /**
   * Retrieves a cached reference to the asset or provides a reference to a placeholder asset.
   *
   * @param storageRef The reference to the asset in cloud storage.
   * @param type The type of the asset.
   * @returns A cached reference to the asset or a reference to a placeholder asset.
   */
  getAssetCachedReference(storageRef: StorageReference, type: AssetType) {
    return this._cachedRefMap.get(storageRef.name) ?? { ref: storageRef, url: `${this._missingAsset}.${type === 'audio' ? 'mp3' : 'svg'}`, cached: false }
  }

  /**
   * Retrieves an asset from cloud storage and caches its reference if necessary.
   *
   * @param type The type of asset. Can be 'audio', 'diagram', 'icon', 'image', or 'video'.
   * @param extension The file extension of the asset.
   * @param name The name of the asset. If provided, it can include a path.
   * @param resolution The resolution of the asset. Applicable for 'icon' type.
   * @param shape The shape of the asset. Applicable for 'icon' type.
   * @returns A Promise resolving to the reference to the asset.
   */
  async getAsset(type: 'audio' | 'diagram' | 'icon' | 'image' | 'video', extension: string, name?: string, resolution?: string, shape: ShapeType = 'round') {
    const storageRef = this.getAssetRef(type, extension, name, resolution, shape)

    // If the asset is locally provided, return its path
    if (!storageRef.storage) {
      return Promise.resolve({ url: storageRef.fullPath } as AssetReference)
    }

    // If the asset is provided by Google Cloud Storage, retrieve its reference
    const found = this.getAssetCachedReference(storageRef, type as AssetType)

    // If the reference is cached, return it; otherwise, fetch the reference and cache it
    return found.cached ? Promise.resolve(found) : this.getDownloadURL(storageRef, found)
  }

  /**
   * Retrieves the download URL for an asset stored in cloud storage.
   *
   * @param storageRef The reference to the asset in cloud storage.
   * @param found The cached reference to the asset.
   * @returns A Promise resolving to the reference to the asset.
   */
  async getDownloadURL(storageRef: StorageReference, found: AssetReference): Promise<AssetReference> {
    try {
      const url = await _getDownloadURL(storageRef)
      if (url) { found.url = url; found.cached = !storageRef.storage; }
      if (found.cached) { this._cachedRefMap.set(storageRef.name, found); }
      return found
    } catch(error) {
      log.debug(error)
      return found
    }
  }

  /**
   * Retrieves an audio asset from cloud storage.
   *
   * @param name The name of the audio asset.
   * @returns A Promise resolving to the reference to the audio asset.
   */
  getAudioAsset(name: string | undefined) {
    return this.getAsset('audio', 'mp3', name)
  }

  /**
   * Retrieves a diagram asset from cloud storage.
   *
   * @param name The name of the diagram asset.
   * @returns A Promise resolving to the reference to the diagram asset.
   */
  getDiagramAsset(name: string | undefined) {
    return this.getAsset('diagram', 'svg', name)
  }

  /**
   * Retrieves an icon asset from cloud storage.
   *
   * @param name The name of the icon asset.
   * @param resolution The resolution of the icon asset.
   * @param shape The shape of the icon asset.
   * @returns A Promise resolving to the reference to the icon asset.
   */
  getIconAsset(name: string | undefined, resolution?: string, shape: ShapeType = 'round') {
    const extension = name?.includes('.') ? name.split('.').pop() ?? 'png' : 'png'
    return this.getAsset('icon', extension, name, resolution, shape)
  }

  /**
   * Retrieves an image asset from cloud storage.
   *
   * @param name The name of the image asset.
   * @returns A Promise resolving to the reference to the image asset.
   */
  getImageAsset(name: string | undefined) {
    return this.getAsset('image', 'png', name)
  }

  /**
   * Retrieves a video asset from cloud storage.
   *
   * @param name The name of the video asset.
   * @returns A Promise resolving to the reference to the video asset.
   */
  getVideoAsset(name: string | undefined) {
    return this.getAsset('video', 'mp4', name)
  }

  /**
   * Retrieves the asset reference for a given resource.
   *
   * @param item The resource containing information about the asset.
   * @returns A Promise resolving to the reference to the asset.
   */
  getAssetForResource(item: Resource) {
    switch (item.assetType) {
      case AssetType.audio: return this.getAudioAsset(item.name)
      case AssetType.video: return this.getVideoAsset(item.name)
      case AssetType.image: return this.getImageAsset(item.name)
      case AssetType.svg: return this.getDiagramAsset(item.name)
      case AssetType.icon: return this.getIconAsset(item.name)
      case AssetType.text: return Promise.resolve({ url: item.name } as AssetReference)
      default: return Promise.resolve({ url: item.name } as AssetReference)
    }
  }

  /**
   * Retrieves and caches asset references for a list of resources.
   *
   * @param resources The list of resources to be assetized.
   * @param andFetch Whether to fetch the assets after assetization.
   * @returns A Promise resolving to the assetized resources.
   */
  assetizeResources(resources: Resource[], andFetch = false, concurrent = 4, onProgress: ((completed: number, total: number) => void) | undefined) {
    return runThroughArguments(
      resources.filter(r => !r.isAssetized),
      r => this.assetizeResource(r, andFetch),
      concurrent,
      onProgress
    )
  }

  /**
   * Assetizes a single resource by retrieving and caching its asset reference.
   *
   * @param r The resource to be assetized.
   * @param andFetch Whether to fetch the asset after assetization.
   * @returns A Promise resolving to the URL of the asset.
   */
  async assetizeResource(r: Resource, andFetch = false) {
    const asset = await this.getAssetForResource(r)
    r.url = asset.url
    r.reference = asset.ref
    return (!andFetch || !['audio', 'image', 'video'].includes(r.assetType ?? '') || !navigator.onLine)
      ? Promise.resolve(r.url)
      : this.preloadResource(r)
  }

  /**
   * Updates the cached reference map with the URL and reference of the asset.
   *
   * @param url The URL of the asset.
   * @param ref The reference to the asset in cloud storage.
   * @returns A Promise resolving to the URL of the asset.
   */
  postload(url: string, ref: StorageReference | undefined) {
    if (ref) this._cachedRefMap.set(ref.name, { ref, url, cached: true })
    return Promise.resolve(url)
  }

  /**
   * Loads the asset into memory before rendering it on the page.
   *
   * @param r The resource to be preloaded.
   * @returns A Promise resolving to the URL of the preloaded asset.
   */
  preloadResource(r: Resource): Promise<string> {
    const ref = this.getRefForAsset(r)
    if (r.url.startsWith(this._missingAsset)) { return Promise.resolve(r.url) }
    const found = ref ? this._cachedRefMap.get(ref.name) : undefined
    if (found?.cached) { return Promise.resolve(r.url) }

    const hidden = document.querySelector('#_hidden')
    if (!hidden) { return Promise.resolve(r.url); }

    // Check if an element for the resource type already exists
    let element: HTMLImageElement | HTMLAudioElement | HTMLVideoElement | null = hidden.querySelector(
      r.assetType === 'image' ? 'img' : r.assetType === 'audio' ? 'audio' : 'video'
    )

    if (!element) {
      switch (r.assetType) {
        case 'image': {
          element = new Image()
          element.onload = () => this.postload(r.url, ref)
          break
        }
        case 'audio': {
          element = new Audio()
          element.setAttribute('muted', 'true')
          element.setAttribute('preload', 'auto')
          element.setAttribute('type', 'audio/mpeg')
          element.oncanplaythrough = () => this.postload(r.url, ref)
          break
        }
        case 'video': {
          element = document.createElement('video')
          element.setAttribute('playsinline', 'true')
          element.setAttribute('muted', 'true')
          element.setAttribute('preload', 'auto')
          element.setAttribute('type', 'video/mp4')
          element.onloadeddata = () => this.postload(r.url, ref)
          break
        }
      }
      if (!element) { return Promise.resolve(r.url) }
      hidden.appendChild(element)
    }
    element.src = r.url
    return Promise.resolve(r.url)
  }

  /**
   * Retrieves the duration of an audio asset using Howler library.
   *
   * @param url The URL of the audio asset.
   * @returns A Promise resolving to the duration of the audio asset in seconds.
   */
  private _audioDuration(url: string) {
    const howl: Howl = new Howl({
      src: [url],
      html5: true,
      format: ['webm', 'mp3', 'aac'],
      preload: 'metadata',
    })
    return new Promise<number>(function(resolve) {
      howl.on('load', () => {
        resolve(howl.duration())
      })
    }).finally(() => howl.unload())
  }

  /**
   * Retrieves a list of files from cloud storage, optionally filtered by a prefix.
   *
   * @param path The path to the directory in cloud storage.
   * @param options Additional options for listing files (can include prefix in options).
   * @param startsWith Optional string to filter files that start with this value.
   *                     If provided, this overrides any prefix in options.
   * @returns A Promise resolving to the list of files in the specified directory.
   */
  async getFileList(
    path: string,
    options: ListOptions = { maxResults: 100 },
    prefix?: string
  ) {
    const listRef = ref(this.storage, path)

    // Use startsWith parameter if provided, otherwise use options.prefix
    const listOptions = { ...options, prefix }

    return list(listRef, listOptions)
  }
}
